/** HATCH — fill the selected boundaries, or change the fill of the selected hatches.
 *
 * ⚠⚠ **ONE DIALOG FOR BOTH, BECAUSE THE SELECTION ALREADY SAYS WHICH.** AutoCAD has HATCH and
 * HATCHEDIT and they ask for the same four things; which one you want is entirely determined by
 * whether you have picked hatches or boundaries. Two dialogs would be two places to keep the
 * pattern list, the scale default and the wording in step.
 *
 * ⚠ The pattern names come from the SERVER (`/api/hatch-patterns/`), which reads ezdxf's own
 * table. A hard-coded list here is how a picker comes to offer a pattern the writer cannot
 * write — and a hatch named for a pattern with no definition opens in AutoCAD as an empty
 * rectangle whose properties palette says otherwise.
 */
import { useEffect, useState } from "react";

import { loadHatchPatterns } from "./api";

export interface HatchSpec {
  pattern: string;
  solid: boolean;
  scale: number;
  angle: number;
  /** ⚠ Set only by BOUNDARY, which reuses this state because it asks the SAME question — "what
   *  encloses this point?" — and `boundary.face_at` answers it once for both. A second
   *  point-collector would be a second gesture for one act. */
  kind?: "boundary";
  /** GRADIENT. ⚠ A gradient is a HATCH with DXF group 450 and up, not a second entity type —
   *  AutoCAD spells it as its own command because its dialog differs, and building it as a
   *  second entity here would double every hatch code path for a fill style. */
  gradient?: { name: string; colour1?: number[] | string; colour2?: number[] | string;
               angle?: number; tint?: number };
}

/** The nine gradients DXF defines. ⚠ Kept in step with `edits.GRADIENTS`, which is the list
 *  that REFUSES anything else — ezdxf will accept any string and write a gradient AutoCAD then
 *  draws as a flat fill, so the refusal is on the server and this is the picker for it. */
const GRADIENTS = ["LINEAR", "CYLINDER", "INVCYLINDER", "SPHERICAL", "INVSPHERICAL",
                   "HEMISPHERICAL", "INVHEMISPHERICAL", "CURVED", "INVCURVED"];

export default function HatchDialog({ editing, count, defaultScale, gradient,
                                      onApply, onClose }: {
  /** True when the selection is hatches — the dialog is CHANGING them, not making one. */
  editing: boolean;
  count: number;
  defaultScale: number;
  onApply: (s: HatchSpec) => void;
  onClose: () => void;
  /** ⚠⚠ GRADIENT, not a second dialog. This asks the same question HATCH does — "what do I
   *  fill the area you are about to click with?" — and AutoCAD splits them only because its two
   *  dialogs differ. Splitting them here would be two places holding the boundary gesture, the
   *  scale default and the not-associative warning. */
  gradient?: boolean;
}) {
  const [all, setAll] = useState<string[]>([]);
  const [common, setCommon] = useState<string[]>([]);
  const [pattern, setPattern] = useState("ANSI31");
  const [solid, setSolid] = useState(false);
  const [scale, setScale] = useState(String(defaultScale));
  const [angle, setAngle] = useState("0");
  const [gname, setGname] = useState("LINEAR");
  const [c1, setC1] = useState("#3070c0");
  // ⚠⚠ **ONE COLOUR IS A REAL AND DIFFERENT FILL, NOT AN EMPTY SECOND SLOT.** AutoCAD's
  // one-colour gradient fades toward the BACKGROUND and its dialog has a tick for it; a
  // two-colour gradient fades between two inks. `two === false` sends no `colour2` at all,
  // which is what `do_hatch` reads to pick between them.
  const [two, setTwo] = useState(false);
  const [c2, setC2] = useState("#ffffff");

  useEffect(() => {
    loadHatchPatterns()
      .then((j) => { setAll(j.patterns); setCommon(j.common); })
      .catch(() => { /* the picker degrades to whatever is typed */ });
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const go = () => {
    const s = Number(scale), a = Number(angle);
    const ang = Number.isFinite(a) ? a : 0;
    onApply({ pattern, solid,
              scale: Number.isFinite(s) && s > 0 ? s : defaultScale,
              angle: ang,
              // ⚠ The gradient carries its OWN angle, because DXF does (group 460) and it means
              // something different: a pattern's angle rotates the hatch lines, a gradient's
              // rotates the sweep. Sharing one field would make rotating a gradient's sweep
              // silently rotate nothing.
              ...(gradient
                ? { gradient: { name: gname, colour1: c1, angle: ang,
                                ...(two ? { colour2: c2 } : {}) } }
                : {}) });
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 430, background: "#3c3f41", border: "1px solid #555",
                    borderRadius: 4, boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          {/* ⚠ THREE STATES, NOT TWO. With nothing picked this is AutoCAD's "Pick internal
              point" half of the prompt — the title said "Hatch Edit — 0 hatches", which is a
              sentence about a command that is not running. */}
          {gradient ? "Gradient — pick internal points"
                    : editing ? `Hatch Edit — ${count} hatch${count === 1 ? "" : "es"}`
                    : count ? `Hatch — ${count} boundar${count === 1 ? "y" : "ies"}`
                            : "Hatch — pick internal points"}
        </div>
        <div style={{ padding: 12, display: "grid", gap: 10, font: "11.5px system-ui" }}>
          {gradient && (
            <div style={{ display: "grid", gap: 9 }}>
              <label style={{ display: "grid", gap: 3 }}>
                <span style={{ color: "#9aa0a6" }}>Gradient</span>
                <select value={gname} onChange={(e) => setGname(e.target.value)}
                        data-testid="grad-name" style={inp}>
                  {GRADIENTS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                <label style={{ display: "grid", gap: 3 }}>
                  <span style={{ color: "#9aa0a6" }}>Colour 1</span>
                  <input type="color" value={c1} data-testid="grad-c1"
                         onChange={(e) => setC1(e.target.value)}
                         style={{ ...inp, padding: 1, width: 56, height: 26 }} />
                </label>
                <label style={{ display: "grid", gap: 3, opacity: two ? 1 : 0.35 }}>
                  <span style={{ color: "#9aa0a6" }}>Colour 2</span>
                  <input type="color" value={c2} data-testid="grad-c2" disabled={!two}
                         onChange={(e) => setC2(e.target.value)}
                         style={{ ...inp, padding: 1, width: 56, height: 26 }} />
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6,
                                paddingBottom: 5 }}>
                  <input type="checkbox" checked={two} data-testid="grad-two"
                         onChange={(e) => setTwo(e.target.checked)} />
                  Two colours
                </label>
              </div>
              {/* ⚠ Said, because the two fills look nothing alike and the tick is easy to miss:
                  one colour fades to the BACKGROUND, two fade between the inks. */}
              <div style={{ color: "#9aa0a6", fontSize: 11 }}>
                {two ? "Fades from colour 1 to colour 2."
                     : "Fades from colour 1 to the background — AutoCAD's default."}
              </div>
              <label style={{ display: "grid", gap: 3 }}>
                <span style={{ color: "#9aa0a6" }}>Angle of the sweep</span>
                <input value={angle} onChange={(e) => setAngle(e.target.value)}
                       data-testid="grad-angle" style={inp} />
              </label>
            </div>
          )}

          {!gradient && <>
          {/* ⚠ SOLID is a checkbox rather than a pattern named "SOLID": in DXF it is a different
              FIELD (group 70), not a pattern name, and offering it in the list would produce a
              hatch that says SOLID and draws lines. */}
          <label style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <input type="checkbox" checked={solid} data-testid="hatch-solid"
                   onChange={(e) => setSolid(e.target.checked)} />
            Solid fill
          </label>

          <div style={{ opacity: solid ? 0.4 : 1, pointerEvents: solid ? "none" : "auto",
                        display: "grid", gap: 8 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {common.map((n) => (
                <button key={n} onClick={() => setPattern(n)} data-testid={`hatch-p-${n}`}
                        style={{ ...btn, padding: "2px 7px",
                                 background: n === pattern ? "#3c5a82" : "#4a4d4f",
                                 borderColor: n === pattern ? "#4a6d9c" : "#5d6164" }}>
                  {n}
                </button>
              ))}
            </div>
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>
                Pattern {all.length ? `(${all.length} available)` : ""}
              </span>
              <select value={pattern} onChange={(e) => setPattern(e.target.value)}
                      data-testid="hatch-pattern" style={inp}>
                {all.length ? all.map((n) => <option key={n} value={n}>{n}</option>)
                            : <option value={pattern}>{pattern}</option>}
              </select>
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ display: "grid", gap: 3, flex: 1 }}>
                {/* ⚠ The scale default comes from the DRAWING, the same 1/200 rule that sizes
                    new text: a pattern at scale 1 on a plan in millimetres is a solid smear. */}
                <span style={{ color: "#9aa0a6" }}>Scale</span>
                <input value={scale} onChange={(e) => setScale(e.target.value)}
                       data-testid="hatch-scale" style={inp} />
              </label>
              <label style={{ display: "grid", gap: 3, flex: 1 }}>
                <span style={{ color: "#9aa0a6" }}>Angle</span>
                <input value={angle} onChange={(e) => setAngle(e.target.value)}
                       data-testid="hatch-angle" style={inp} />
              </label>
            </div>
          </div>
          </>}

          {/* ⚠⚠ SAID OUT LOUD RATHER THAN DISCOVERED. AutoCAD's associative hatch follows its
              boundary when the boundary moves; this one does not, and a drafter who moves a wall
              and finds the poché left behind should have been told before they started. */}
          {!editing && (
            <div style={{ color: "#c8a35a", fontSize: 11, lineHeight: 1.5 }}>
              ⚠ Not associative — moving the boundary later leaves the fill behind.
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 2 }}>
            <button style={btn} onClick={onClose} data-testid="hatch-cancel">Cancel</button>
            <button style={{ ...btn, background: "#3c5a82", borderColor: "#4a6d9c" }}
                    data-testid="hatch-ok" onClick={go}>
              {gradient ? "Fill" : editing ? "Apply" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "4px 7px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
